import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { Control, FieldPath, FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";

import CloseIcon from "src/core/assets/images/icons/close.svg?react";
import { Spinner } from "src/core/components/custom-ui/spinner";
import { Button } from "src/core/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "src/core/components/ui/command";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "src/core/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "src/core/components/ui/popover";
import { ISelectOption } from "src/core/constants/options";
import { Roles } from "src/core/constants/roles";
import { USER_SEARCH_MIN_QUERY_LEN, useOwnerOptions } from "src/core/hooks/common/useOwnerOptions";
import { cn } from "src/core/lib/utils";

const DEFAULT_COLLABORATOR_GROUPS = [Roles.COLLABORATEUR];

type FormUserSearchBaseProps<T extends FieldValues> = {
  name: FieldPath<T>;
  label?: string;
  control?: Control<T>;
  allowedGroups?: string[];
  pinnedOptions?: ISelectOption[];
  placeholder?: string;
  className?: string;
  wrapperClassName?: string;
  disabled?: boolean;
  isInModal?: boolean;
  required?: boolean;
  softRequired?: boolean;
  onSelected?: (option: ISelectOption | null) => void;
};

type FormUserSearchProps<T extends FieldValues> = FormUserSearchBaseProps<T> & {
  clearable?: boolean;
};

type FormUserSearchMultiProps<T extends FieldValues> = FormUserSearchBaseProps<T>;

type UserSearchListProps = {
  id: string;
  search: string;
  onSearchChange: (value: string) => void;
  options: ISelectOption[];
  loading: boolean;
  disabled?: boolean;
  clearable?: boolean;
  onSelect: (option: ISelectOption) => void;
  onClear?: () => void;
};

function UserSearchList({
  id,
  search,
  onSearchChange,
  options,
  loading,
  disabled = false,
  clearable = false,
  onSelect,
  onClear,
}: UserSearchListProps) {
  const { t } = useTranslation();
  const trimmed = search.trim();
  const canSearch = trimmed.length >= USER_SEARCH_MIN_QUERY_LEN;
  const showResults = canSearch && !loading && options.length > 0;

  return (
    <Command shouldFilter={false}>
      <CommandInput
        placeholder={t("global.form.userSearch.searchPlaceholder", {
          count: USER_SEARCH_MIN_QUERY_LEN,
        })}
        value={search}
        onValueChange={onSearchChange}
        disabled={disabled}
      />
      {canSearch && !showResults && (
        <p className="py-6 text-center font-light text-sm">
          {loading ? (
            <span className="flex justify-center">
              <Spinner size="sm" />
            </span>
          ) : (
            t("global.form.userSearch.empty")
          )}
        </p>
      )}
      <CommandList id={`${id}-listbox`}>
        <CommandEmpty className="hidden" />
        <CommandGroup>
          {clearable && trimmed.length > 0 && (
            <CommandItem value="__none__" onSelect={() => onClear?.()}>
              <span className="font-light text-primary/50">{t("global.form.select.none")}</span>
            </CommandItem>
          )}
          {showResults &&
            options.map((option) => (
              <CommandItem
                key={option.value}
                value={option.value}
                onSelect={() => onSelect(option)}
                disabled={disabled}
              >
                <span className="font-light truncate">{option.label}</span>
              </CommandItem>
            ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

export function FormUserSearch<T extends FieldValues>({
  name,
  label = "",
  control,
  allowedGroups,
  pinnedOptions,
  placeholder,
  className = "w-[360px]",
  wrapperClassName = "",
  disabled = false,
  isInModal = true,
  required = false,
  softRequired = false,
  clearable = false,
  onSelected,
}: FormUserSearchProps<T>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { options, loading, search, setSearch, rememberOption, labelFor } = useOwnerOptions(
    allowedGroups,
    pinnedOptions
  );

  const close = () => {
    setOpen(false);
    setSearch("");
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const selectedId = typeof field.value === "string" ? field.value : "";
        const selectedLabel = labelFor(selectedId);
        const isInvalid = fieldState.invalid;
        const showSoftRequired = softRequired && !isInvalid;

        return (
          <FormItem className={wrapperClassName}>
            {label !== "" && (
              <FormLabel required={required} className={cn(isInvalid && "text-alert")}>
                {label}
              </FormLabel>
            )}
            <Popover
              open={open}
              modal={isInModal}
              onOpenChange={(next) => {
                setOpen(next);
                if (!next) setSearch("");
              }}
            >
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    type="button"
                    variant="combobox"
                    role="combobox"
                    aria-expanded={open}
                    aria-controls={`${name}-listbox`}
                    disabled={disabled}
                    data-testid={`user-search-${name}`}
                    className={cn(
                      "w-full justify-between",
                      className,
                      isInvalid && "border-alert",
                      showSoftRequired && "border-2 border-dashed border-orange-medium",
                      disabled && "bg-background text-muted-foreground"
                    )}
                  >
                    {selectedLabel ? (
                      <span className={cn("truncate", disabled && "opacity-50")}>
                        {selectedLabel}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">
                        {placeholder || t("global.form.select.placeholder")}
                      </span>
                    )}
                    <ChevronDown />
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent
                className="w-[var(--radix-popover-trigger-width)] p-0"
                align="start"
                side="bottom"
              >
                <UserSearchList
                  id={name}
                  search={search}
                  onSearchChange={setSearch}
                  options={options}
                  loading={loading}
                  disabled={disabled}
                  clearable={clearable}
                  onClear={() => {
                    field.onChange("");
                    onSelected?.(null);
                    close();
                  }}
                  onSelect={(option) => {
                    rememberOption(option);
                    field.onChange(option.value);
                    onSelected?.(option);
                    close();
                  }}
                />
              </PopoverContent>
            </Popover>
            <FormMessage />
            {showSoftRequired && (
              <p className="font-light text-sm text-muted">
                {t("global.form.warning.softRequired")}
              </p>
            )}
          </FormItem>
        );
      }}
    />
  );
}

export function FormUserSearchMulti<T extends FieldValues>({
  name,
  label = "",
  control,
  allowedGroups = DEFAULT_COLLABORATOR_GROUPS,
  pinnedOptions,
  placeholder,
  className = "w-[360px]",
  wrapperClassName = "",
  disabled = false,
  isInModal = true,
  required = false,
  softRequired = false,
}: FormUserSearchMultiProps<T>) {
  const { t } = useTranslation();
  const { options, loading, search, setSearch, rememberOption, labelFor } = useOwnerOptions(
    allowedGroups,
    pinnedOptions
  );

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const selectedValues = (field.value as string[]) || [];
        const isInvalid = fieldState.invalid;
        const showSoftRequired = softRequired && !isInvalid;
        const availableOptions = options.filter((option) => !selectedValues.includes(option.value));

        const handleSelect = (option: ISelectOption) => {
          rememberOption(option);
          if (!selectedValues.includes(option.value)) {
            field.onChange([...selectedValues, option.value]);
          }
          setSearch("");
        };

        const handleRemove = (valueToRemove: string) => {
          field.onChange(selectedValues.filter((value) => value !== valueToRemove));
        };

        return (
          <FormItem className={cn("min-w-0", className, wrapperClassName)}>
            {label !== "" && (
              <FormLabel required={required} className={cn(isInvalid && "text-alert")}>
                {label}
              </FormLabel>
            )}
            <div className="flex flex-col gap-y-10 min-w-0 w-full">
              <Popover
                modal={isInModal}
                onOpenChange={(open) => {
                  if (!open) setSearch("");
                }}
              >
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      type="button"
                      variant="combobox"
                      role="combobox"
                      aria-controls={`${name}-listbox`}
                      disabled={disabled}
                      data-testid={`user-search-${name}`}
                      className={cn(
                        "w-full justify-between",
                        isInvalid && "border-alert",
                        showSoftRequired && "border-2 border-dashed border-orange-medium"
                      )}
                    >
                      <span className="text-muted-foreground">
                        {placeholder || t("global.form.select.placeholder")}
                      </span>
                      <ChevronDown />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                  align="start"
                  side="bottom"
                >
                  <UserSearchList
                    id={name}
                    search={search}
                    onSearchChange={setSearch}
                    options={availableOptions}
                    loading={loading}
                    disabled={disabled}
                    onSelect={handleSelect}
                  />
                </PopoverContent>
              </Popover>

              {selectedValues.length > 0 && (
                <div className="flex flex-row flex-wrap gap-x-10 gap-y-10 min-w-0 w-full">
                  {selectedValues.map((value) => {
                    const optionLabel = labelFor(value);
                    if (!optionLabel) return null;

                    return (
                      <div
                        key={value}
                        className="flex flex-row items-center gap-x-10 px-10 w-fit max-w-full min-w-0 h-20 rounded-lg border border-blue-completed font-light text-blue-completed text-xs"
                      >
                        <span className="truncate" title={optionLabel}>
                          {optionLabel}
                        </span>
                        <button
                          type="button"
                          className="shrink-0"
                          onClick={() => handleRemove(value)}
                          disabled={disabled}
                          aria-label={`remove ${optionLabel}`}
                        >
                          <CloseIcon className="h-10 w-10 text-blue-info" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <FormMessage />
            {showSoftRequired && (
              <p className="font-light text-sm text-muted">
                {t("global.form.warning.softRequired")}
              </p>
            )}
          </FormItem>
        );
      }}
    />
  );
}
